import { api } from '@/shared/api/client'
import { qs } from '@/shared/lib/query'

/**
 * Bulk-loading a nomination list.
 *
 * ## Three calls, and the middle one is the point
 *
 * `upload` registers the file, `dryRun` produces a verdict with **nothing written**, and
 * `apply` writes it. A general election's nomination list is tens of thousands of rows that
 * cannot be unpicked by hand, so the moment where a human reads what is about to be created
 * is not an optional convenience — it is the only safeguard there is.
 *
 * ## A scanned form is the same three calls with one step in front
 *
 * `uploadForm` posts an image or a PDF and returns 202 with an `ocrJobId` — the rows do not
 * exist yet, because an engine has to read the page first and after a restart that waits for
 * a model warm-up. `dryRun` answers **409 while the extraction is unfinished**, which is why
 * this screen polls the job instead of the batch: "come back in a moment" and "this form
 * cannot be read" are different answers and must not look the same.
 *
 * Everything after the extraction is IDENTICAL to the spreadsheet path — the server hands a
 * scanned table to the same validator, so there is one set of rules and one set of messages.
 */

export interface ImportBatch {
  id: string
  electionId: string
  fileId: string | null
  sourceCode: string
  /** UPLOADED → PARSING → VALIDATED → APPLIED, or FAILED. */
  statusCode: string
  /** The extraction these rows came from, for an `OCR_FORM` batch. Null for a spreadsheet. */
  ocrJobId: string | null
  totalRows: number
  validRows: number
  rejectedRows: number
  appliedRows: number
  dryRunAt: string | null
  appliedAt: string | null
  appliedBy: string | null
  /** Set when the FILE could not be used at all — bad CSV, missing columns, too many rows. */
  error: string | null
  notes: string | null
  createdAt: string
}

export interface ImportRow {
  id: string
  rowNumber: number
  /** The row as uploaded, verbatim. JSON string — parse to render. */
  raw: string
  /** Our reading of it, or null while rejected. JSON string. */
  parsed: string | null
  statusCode: string
  /** `[{field, message}]` as JSON — EVERY problem the row has, not the first. */
  problems: string | null
  candidacyId: string | null
}

export const listImportBatches = (electionId: string) =>
  api.get<ImportBatch[]>(`/candidacy-imports${qs({ electionId })}`)

export const getImportBatch = (id: string) =>
  api.get<ImportBatch>(`/candidacy-imports/${id}`)

export const listImportRows = (
  id: string, opts: { status?: string | null; size?: number; offset?: number } = {},
) => api.get<ImportRow[]>(`/candidacy-imports/${id}/rows${qs({ ...opts })}`)

export const createImportBatch = (body: {
  electionId: string
  fileId: string
  notes?: string | null
}) => api.post<ImportBatch>('/candidacy-imports', body)

/**
 * Register a SCANNED nomination form and queue its extraction.
 *
 * 202, not 201: the batch exists and its rows do not. Poll {@link getOcrJob} with the
 * returned `ocrJobId` before calling {@link dryRunImport}.
 */
export const uploadFormBatch = (body: {
  electionId: string
  fileId: string
  notes?: string | null
}) => api.post<ImportBatch>('/candidacy-imports/form', body)

/**
 * Read the form again, as a NEW extraction.
 *
 * For the failures a person can fix — the scanner was on greyscale, the page went in upside
 * down, the engine was still warming up. The previous attempt is left where it is: the server
 * never overwrites a failed extraction, because it is the record that this form was offered
 * to the machine and could not be read.
 */
export const reExtractImport = (id: string) =>
  api.post<ImportBatch>(`/candidacy-imports/${id}/re-extract`)

export interface OcrJob {
  id: string
  fileId: string
  purposeCode: string
  /** QUEUED → RUNNING → SUCCEEDED, or FAILED. */
  statusCode: string
  engine: string | null
  pageCount: number | null
  meanConfidence: number | null
  attempts: number
  error: string | null
  startedAt: string | null
  finishedAt: string | null
}

export const getOcrJob = (id: string) => api.get<OcrJob>(`/ocr/jobs/${id}`)

/** Whether the sidecar is deployed here, so a screen can offer manual entry instead. */
export const ocrHealth = () =>
  api.get<{ reachable: boolean; defaultEngine: string | null; engines: string[] }>('/ocr/health')

/** Validate every row. Writes no candidacies — re-runnable against a corrected file. */
export const dryRunImport = (id: string) =>
  api.post<ImportBatch>(`/candidacy-imports/${id}/dry-run`)

/** Write the valid rows. Only a batch that has been dry-run may be applied. */
export const applyImport = (id: string) =>
  api.post<ImportBatch>(`/candidacy-imports/${id}/apply`)

/** `[{field, message}]`, parsed defensively — a malformed blob must not blank the row. */
export function parseProblems(json: string | null): { field: string; message: string }[] {
  if (!json) return []
  try {
    const v = JSON.parse(json)
    return Array.isArray(v) ? v : []
  } catch {
    return [{ field: '', message: json }]
  }
}

/**
 * The raw row as a plain object, for rendering the columns as uploaded.
 *
 * `_ocr` is stripped: it is provenance the server attaches to a scanned row, not a column
 * anybody typed, and showing it among the fields would send a reviewer looking for it in
 * their spreadsheet. Read it with {@link parseOcr} instead.
 */
export function parseRaw(json: string | null): Record<string, string> {
  if (!json) return {}
  try {
    const v = JSON.parse(json)
    if (!v || typeof v !== 'object') return {}
    const { _ocr: _ignored, ...columns } = v as Record<string, unknown>
    return columns as Record<string, string>
  } catch {
    return {}
  }
}

/**
 * What the machine made of a scanned row: the page it came off, and how sure it was of each
 * cell.
 *
 * ## Confidence is highlighted, never enforced
 *
 * It is the engine's opinion of its own reading, not a probability that the value is right.
 * Nothing in this UI may drop or reject a row because of it — the whole point is that a
 * HUMAN decides, with the machine's doubt visible while they do.
 */
export interface OcrRowProvenance {
  page: number
  /** The weakest cell that carries text — what to look at first. */
  minConfidence: number
  confidence: Record<string, number>
}

export function parseOcr(json: string | null): OcrRowProvenance | null {
  if (!json) return null
  try {
    const v = JSON.parse(json) as { _ocr?: OcrRowProvenance }
    return v && typeof v === 'object' && v._ocr ? v._ocr : null
  } catch {
    return null
  }
}

/** Below this a cell is worth a second look. Not a threshold anything is rejected on. */
export const LOW_CONFIDENCE = 0.75
